/**
 * Configurações do servidor: identidade, convites e cargos.
 *
 * O convite é a primeira aba porque é a ação mais frequente: quase todo acesso
 * a esta tela é para pegar um link e mandar para alguém.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Copy, Link2, Plus, Settings2, Shield, Trash2, Upload } from 'lucide-react'
import type { Invite, Role } from '@nexus/shared'
import {
  PERMISSION_GROUPS,
  PERMISSION_LABELS,
  Permission,
  ROLE_COLORS,
  avatarColor,
  formatRelativeTime,
  fromBitString,
  hasPermission,
  initials,
  toBitString,
} from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api, ApiError } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { useGuildPermissions, useGuildRoles } from '@/hooks/selectors'
import { Button, Field, Input, Modal, Spinner, Switch, Textarea } from '@/components/ui'

type Tab = 'invites' | 'general' | 'roles'

export function GuildSettingsModal({
  open,
  guildId,
  /** Em qual aba abrir. O menu da barra lateral entra direto em convites. */
  abaInicial = 'invites',
  onClose,
}: {
  open: boolean
  guildId: string
  abaInicial?: Tab
  onClose: () => void
}) {
  const [tab, setTab] = useState<Tab>(abaInicial)

  // Sem isto, quem esteve em cargos e depois pede "convidar pessoas" cai de
  // volta em cargos: o estado da aba sobrevive ao fechamento do modal.
  useEffect(() => {
    if (open) setTab(abaInicial)
  }, [open, abaInicial])

  const tabs: { id: Tab; label: string; icon: typeof Link2 }[] = [
    { id: 'invites', label: 'Convites', icon: Link2 },
    { id: 'general', label: 'Geral', icon: Settings2 },
    { id: 'roles', label: 'Cargos', icon: Shield },
  ]

  return (
    <Modal open={open} onClose={onClose} title="Configurações do servidor" size="lg">
      <div className="mb-5 flex gap-1 border-b border-line">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === id
                ? 'border-brand text-content-primary'
                : 'border-transparent text-content-secondary hover:text-content-primary',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'invites' ? <InvitesTab guildId={guildId} /> : null}
      {tab === 'general' ? <GeneralTab guildId={guildId} /> : null}
      {tab === 'roles' ? <RolesTab guildId={guildId} /> : null}
    </Modal>
  )
}

function InvitesTab({ guildId }: { guildId: string }) {
  const [invites, setInvites] = useState<Invite[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const { invites: list } = await api.invites.listForGuild(guildId)
      setInvites(list)
    } catch {
      setInvites([])
    }
  }, [guildId])

  useEffect(() => {
    void load()
  }, [load])

  const handleCreate = async () => {
    setCreating(true)
    try {
      await api.invites.create(guildId, { maxAgeSeconds: 604800, maxUses: 0 })
      await load()
    } finally {
      setCreating(false)
    }
  }

  const copyLink = async (code: string) => {
    const url = `${location.origin}/entrar/${code}`
    await navigator.clipboard.writeText(url)
    setCopied(code)
    setTimeout(() => setCopied(null), 2000)
  }

  if (invites === null) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="size-6 text-content-muted" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-content-secondary">
          Qualquer pessoa com o link entra no servidor.
        </p>
        <Button size="sm" onClick={() => void handleCreate()} loading={creating}>
          <Plus className="size-4" />
          Gerar convite
        </Button>
      </div>

      {invites.length === 0 ? (
        <p className="rounded-lg bg-surface-base py-8 text-center text-sm text-content-muted">
          Nenhum convite ativo. Gere um para começar a convidar pessoas.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {invites.map((invite) => (
            <li
              key={invite.code}
              className="flex items-center gap-3 rounded-lg bg-surface-base p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-[13px] text-content-primary">
                  {location.host}/entrar/{invite.code}
                </p>
                <p className="mt-0.5 text-[11px] text-content-muted">
                  {invite.uses} {invite.uses === 1 ? 'uso' : 'usos'}
                  {invite.maxUses > 0 ? ` de ${invite.maxUses}` : ''}
                  {invite.expiresAt
                    ? ` · expira ${formatRelativeTime(invite.expiresAt)}`
                    : ' · não expira'}
                </p>
              </div>

              <Button size="icon-sm" variant="ghost" onClick={() => void copyLink(invite.code)}>
                {copied === invite.code ? (
                  <Check className="size-4 text-success" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>

              <Button
                size="icon-sm"
                variant="danger-ghost"
                onClick={async () => {
                  await api.invites.revoke(invite.code)
                  await load()
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function GeneralTab({ guildId }: { guildId: string }) {
  const guild = useAppStore((s) => s.guilds[guildId])
  const applyEvent = useAppStore((s) => s.applyEvent)
  const [name, setName] = useState(guild?.name ?? '')
  const [description, setDescription] = useState(guild?.description ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enviandoImagem, setEnviandoImagem] = useState(false)
  const arquivoRef = useRef<HTMLInputElement>(null)

  /*
    Só quem administra o servidor mexe na identidade dele.

    A tela toda continua acessível, porque a aba de convites é o motivo mais
    comum de alguém chegar aqui; o que muda é que os controles ficam apagados em
    vez de aceitarem o clique e devolverem um erro do servidor.
  */
  const permissoes = useGuildPermissions(guildId)
  const podeGerenciar = hasPermission(permissoes, Permission.MANAGE_GUILD)

  if (!guild) return null

  const escolherImagem = async (arquivo: File) => {
    setEnviandoImagem(true)
    setError(null)
    try {
      const { guild: atualizado } = await api.guilds.uploadIcon(guildId, arquivo)
      applyEvent({ op: 'guild_update', d: atualizado, s: 0 })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível enviar a imagem')
    } finally {
      setEnviandoImagem(false)
    }
  }

  const removerImagem = async () => {
    setEnviandoImagem(true)
    setError(null)
    try {
      const atualizado = await api.guilds.update(guildId, { iconUrl: null })
      applyEvent({ op: 'guild_update', d: atualizado, s: 0 })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível remover a imagem')
    } finally {
      setEnviandoImagem(false)
    }
  }

  const dirty = name !== guild.name || description !== (guild.description ?? '')

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.guilds.update(guildId, { name: name.trim(), description: description.trim() || null })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Field
        label="Imagem do servidor"
        hint="Aparece na barra da esquerda no lugar das iniciais. O recorte é quadrado."
      >
        <div className="flex items-center gap-4">
          {/* A prévia é a bolinha de verdade, no mesmo tamanho e formato em que
              ela vai aparecer, para não haver surpresa depois de salvar. */}
          <div
            className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-lg font-semibold text-white"
            style={guild.iconUrl ? undefined : { backgroundColor: avatarColor(guild.id) }}
          >
            {guild.iconUrl ? (
              <img src={guild.iconUrl} alt="" className="size-full object-cover" />
            ) : (
              initials(guild.name)
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={enviandoImagem}
              disabled={!podeGerenciar}
              onClick={() => arquivoRef.current?.click()}
            >
              <Upload className="size-4" />
              {guild.iconUrl ? 'Trocar imagem' : 'Enviar imagem'}
            </Button>

            {guild.iconUrl ? (
              <Button
                variant="danger-ghost"
                size="sm"
                disabled={enviandoImagem || !podeGerenciar}
                onClick={() => void removerImagem()}
              >
                Remover
              </Button>
            ) : null}
          </div>

          <input
            ref={arquivoRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const arquivo = e.target.files?.[0]
              if (arquivo) void escolherImagem(arquivo)
              e.target.value = ''
            }}
          />
        </div>
      </Field>

      <Field label="Nome do servidor" error={error ?? undefined}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          disabled={!podeGerenciar}
        />
      </Field>

      <Field label="Descrição">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={512}
          disabled={!podeGerenciar}
        />
      </Field>

      <div className="flex items-center justify-end gap-3">
        {saved ? <span className="text-sm text-success">Salvo</span> : null}
        <Button
          onClick={() => void handleSave()}
          loading={saving}
          disabled={!dirty || !podeGerenciar}
        >
          Salvar alterações
        </Button>
      </div>
    </div>
  )
}

function RolesTab({ guildId }: { guildId: string }) {
  const roles = useGuildRoles(guildId)

  const [selectedId, setSelectedId] = useState<string | null>(roles[0]?.id ?? null)
  const selected = roles.find((r) => r.id === selectedId) ?? roles[0]

  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    setCreating(true)
    try {
      const role = await api.guilds.createRole(guildId, { name: 'Novo cargo', color: ROLE_COLORS[0] })
      setSelectedId(role.id)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex gap-4">
      <div className="w-48 shrink-0">
        <Button size="sm" full onClick={() => void handleCreate()} loading={creating}>
          <Plus className="size-4" />
          Criar cargo
        </Button>

        <ul className="mt-2 flex flex-col gap-0.5">
          {roles.map((role) => (
            <li key={role.id}>
              <button
                type="button"
                onClick={() => setSelectedId(role.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  role.id === selected?.id
                    ? 'bg-surface-active text-content-primary'
                    : 'text-content-secondary hover:bg-surface-hover',
                )}
              >
                <span
                  className="size-3 shrink-0 rounded-full"
                  style={{ backgroundColor: role.color ?? 'var(--color-content-muted)' }}
                />
                <span className="truncate">{role.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="min-w-0 flex-1">
        {selected ? <RoleEditor key={selected.id} guildId={guildId} role={selected} /> : null}
      </div>
    </div>
  )
}

function RoleEditor({ guildId, role }: { guildId: string; role: Role }) {
  const [name, setName] = useState(role.name)
  const [color, setColor] = useState(role.color ?? ROLE_COLORS[0])
  const [permissions, setPermissions] = useState(() => fromBitString(role.permissions))
  const [hoist, setHoist] = useState(role.hoist)
  const [saving, setSaving] = useState(false)

  const togglePermission = (bit: bigint) => {
    setPermissions((current) => (current & bit ? current & ~bit : current | bit))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.guilds.updateRole(guildId, role.id, {
        name: name.trim(),
        color,
        permissions: toBitString(permissions),
        hoist,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {!role.isEveryone ? (
        <>
          <Field label="Nome do cargo">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={48} />
          </Field>

          <Field label="Cor">
            <div className="flex flex-wrap gap-1.5">
              {ROLE_COLORS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColor(option)}
                  aria-label={`Cor ${option}`}
                  className={cn(
                    'size-7 rounded-md transition-transform',
                    color === option ? 'ring-2 ring-content-primary ring-offset-2 ring-offset-surface-raised' : 'hover:scale-110',
                  )}
                  style={{ backgroundColor: option }}
                />
              ))}
            </div>
          </Field>

          <Switch
            checked={hoist}
            onChange={setHoist}
            label="Exibir separadamente"
            description="Membros com este cargo aparecem num grupo próprio na lista."
          />
        </>
      ) : (
        <p className="rounded-lg bg-surface-base p-3 text-sm text-content-secondary">
          O cargo @everyone define o que todo membro pode fazer por padrão.
        </p>
      )}

      <div>
        <h4 className="mb-2 text-xs font-semibold tracking-wide text-content-secondary uppercase">
          Permissões
        </h4>

        <div className="max-h-64 overflow-y-auto rounded-lg bg-surface-base p-3">
          {PERMISSION_GROUPS.map((group) => (
            <section key={group.label} className="mb-4 last:mb-0">
              <h5 className="mb-1 text-[11px] font-bold tracking-wider text-content-muted uppercase">
                {group.label}
              </h5>
              {group.keys.map((key) => {
                const bit = Permission[key]
                return (
                  <Switch
                    key={key}
                    checked={(permissions & bit) === bit}
                    onChange={() => togglePermission(bit)}
                    label={PERMISSION_LABELS[key]}
                  />
                )
              })}
            </section>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => void handleSave()} loading={saving}>
          Salvar cargo
        </Button>
      </div>
    </div>
  )
}
